import { useState } from 'react';
import PropTypes from 'prop-types';
import { familyApi } from '../../services/api';
import './JoinFamilyForm.css';

const JoinFamilyForm = ({ onFamilyJoined, onCancel }) => {
  const [inviteCode, setInviteCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    setInviteCode(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!inviteCode.trim()) {
      setError('Invite code is required');
      return;
    }

    if (inviteCode.length < 6) {
      setError('Invite code must be at least 6 characters');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      await familyApi.joinFamily(inviteCode);
      onFamilyJoined();
    } catch (err) {
      console.error('Error joining family:', err);
      setError(err.response?.data?.error || 'Failed to join family');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="join-family-form">
      <div className="form-header">
        <h3>Join a family</h3>
        <p>Enter the invite code shared by a family member to join their family.</p>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="inviteCode">
            Invite code *
          </label>
          <input
            type="text"
            id="inviteCode"
            value={inviteCode}
            onChange={handleInputChange}
            placeholder="Enter invite code"
            maxLength={20}
            required
            disabled={loading}
            className="invite-code-input"
          />
          <small className="form-text">
            Enter the code exactly as provided by the family creator.
          </small>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || !inviteCode.trim() || inviteCode.length < 6}
          >
            {loading ? 'Joining...' : 'Join a family'}
          </button>
        </div>
      </form>

      <div className="form-info">
        <h4>What happens when you join?</h4>
        <ul>
          <li>You will become a member of the family</li>
          <li>All your readers will be added to the family</li>
          <li>You can log reading for any reader in the family</li>
          <li>Other family members can log reading for your readers</li>
          <li>You can leave the family at any time</li>
        </ul>
      </div>

      <div className="help-section">
        <h4>Need help?</h4>
        <p>
          Ask the family creator to share their invite code with you. 
          You can find the invite code in the family card after creating or joining a family.
        </p>
      </div>
    </div>
  );
};

JoinFamilyForm.propTypes = {
  onFamilyJoined: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default JoinFamilyForm;
